import {
  doesFocusableExist,
  getCurrentFocusKey,
  setFocus,
} from '@noriginmedia/norigin-spatial-navigation';
import { useEffect, useRef } from 'react';

import {
  activeFocusKeys,
  chromeFocusKeys,
  diagnoseFocus,
  DOM_ONLY_FAULTS,
  type FocusRepair,
  type FocusSnapshot,
  getScreenFocusDefault,
  isFault,
  managedFocusKeys,
  pickFocusTarget,
  scopeDefaultFocusKey,
  staleActiveKey,
} from './focusWatchdog';
import { isTvNavigationPaused, resumeTvNavigation } from './initTvNavigation';

export interface UseFocusWatchdogOptions {
  enabled?: boolean;
  /** Reported on every repair (dev overlay + Mixpanel, docs/04 §2.1). */
  onRepair?: (repair: FocusRepair) => void;
  /**
   * How long to let norigin's scheduler settle before judging a snapshot. Its focus changes
   * are asynchronous, so an immediate check would see faults that fix themselves.
   */
  settleMs?: number;
  /** Heartbeat for the failure modes nothing else announces (0 disables it). */
  intervalMs?: number;
  /** Minimum gap between two repairs; stops a repair loop from spinning. */
  cooldownMs?: number;
}

export const FOCUS_SETTLE_MS = 120;
export const FOCUS_INTERVAL_MS = 750;
export const FOCUS_COOLDOWN_MS = 400;

function takeSnapshot(): FocusSnapshot {
  const current = getCurrentFocusKey() ?? null;
  return {
    current,
    exists: current ? doesFocusableExist(current) : false,
    paused: isTvNavigationPaused(),
    candidates: managedFocusKeys(document),
    chrome: chromeFocusKeys(document),
    active: activeFocusKeys(document),
  };
}

/**
 * Keeps exactly one managed focusable focused, for the whole app (QA3 T1).
 *
 * Mount it once, in the shell. It re-checks after every key press, after every DOM change (a
 * re-render, an overlay opening or closing, a list being replaced) and on a slow heartbeat for
 * anything that announces itself in neither — and when the check fails it moves the focus back
 * to the last healthy element, the first focusable of the screen, or the screen's default, in
 * that order. Every repair is reported through `onRepair` so it is visible instead of silent.
 *
 * The checks are debounced by `settleMs`: norigin applies focus asynchronously, so a snapshot
 * taken in the same tick as a render legitimately shows "nothing focused".
 */
export function useFocusWatchdog({
  enabled = true,
  onRepair,
  settleMs = FOCUS_SETTLE_MS,
  intervalMs = FOCUS_INTERVAL_MS,
  cooldownMs = FOCUS_COOLDOWN_MS,
}: UseFocusWatchdogOptions = {}): void {
  const report = useRef(onRepair);
  useEffect(() => {
    report.current = onRepair;
  });

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return undefined;

    let lastGood: string | null = null;
    let lastRepairAt = 0;
    let domFaults = 0;
    let pending: number | null = null;

    const check = () => {
      pending = null;
      const snapshot = takeSnapshot();
      const fault = diagnoseFocus(snapshot);

      if (!isFault(fault)) {
        if (fault === 'none' && snapshot.current) lastGood = snapshot.current;
        domFaults = 0;
        return;
      }

      // A fault only the DOM can see may be one render behind; confirm it once more first.
      if (DOM_ONLY_FAULTS.includes(fault)) {
        domFaults += 1;
        if (domFaults < 2) {
          schedule(settleMs);
          return;
        }
      }
      domFaults = 0;

      const now = Date.now();
      if (now - lastRepairAt < cooldownMs) {
        schedule(cooldownMs);
        return;
      }

      const { screen, defaultFocusKey } = getScreenFocusDefault();

      // Key handling was disabled as a side effect and never turned back on: nothing else can
      // recover from this, because the keys that would trigger a repair are the ones swallowed.
      if (fault === 'paused') {
        resumeTvNavigation();
        lastRepairAt = now;
        report.current?.({
          screen,
          fault,
          from: snapshot.current,
          to: snapshot.current,
          strategy: 'resume',
        });
        schedule(settleMs);
        return;
      }

      // The DOM disagrees about who is focused but norigin's key is perfectly good — a render
      // that lagged behind. Re-assert the same key instead of *moving* the focus: moving it
      // would drag the user off the button they were about to press.
      if (
        DOM_ONLY_FAULTS.includes(fault) &&
        snapshot.current &&
        snapshot.exists &&
        snapshot.candidates.includes(snapshot.current)
      ) {
        lastRepairAt = now;
        // A *second* node left marked (UX-046) is the one case re-asserting cannot cure: norigin
        // only blurs the node it believes is focused, and `setFocus(current)` short-circuits when
        // the key is already current — so the extra `data-focused` would survive every repair and
        // the room would keep seeing two highlights. Parking the focus on the stale node for one
        // turn makes it the "old" one, so bringing the focus back blurs it for real.
        const stale = staleActiveKey(snapshot);
        const current = snapshot.current;
        if (stale && doesFocusableExist(stale)) {
          void setFocus(stale).then(() => setFocus(current));
        } else {
          void setFocus(current);
        }
        report.current?.({
          screen,
          fault,
          from: snapshot.current,
          to: snapshot.current,
          strategy: 'resync',
        });
        return;
      }

      const { target, strategy } = pickFocusTarget(snapshot, {
        last: lastGood,
        preferred: scopeDefaultFocusKey(document),
        fallback: defaultFocusKey,
      });
      if (!target) return;

      lastRepairAt = now;
      lastGood = target;
      void setFocus(target);
      report.current?.({ screen, fault, from: snapshot.current, to: target, strategy });
    };

    function schedule(delay = settleMs): void {
      if (pending !== null) window.clearTimeout(pending);
      pending = window.setTimeout(check, delay);
    }

    const onKey = () => schedule();
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('keyup', onKey, true);

    // Every mount/unmount of a focusable shows up here: a list narrowing, an overlay closing,
    // an async re-render. It is the "after every render" half of the contract.
    const observer = new MutationObserver(() => schedule());
    observer.observe(document.body, { childList: true, subtree: true, attributes: false });

    const heartbeat = intervalMs > 0 ? window.setInterval(() => schedule(0), intervalMs) : null;
    schedule();

    return () => {
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('keyup', onKey, true);
      observer.disconnect();
      if (heartbeat !== null) window.clearInterval(heartbeat);
      if (pending !== null) window.clearTimeout(pending);
    };
  }, [enabled, settleMs, intervalMs, cooldownMs]);
}
